import React, { useEffect, useState } from "react";

/**
 * KPI 슬롯 추가 모달 — 튜토리얼식 위저드. SMART_WORKSHOP(sbizType)/SMART_FACTORY(kpiType) 공용.
 *
 * Step 1: 지표 선택 (WORKSHOP=sbizType, FACTORY=kpiType — 부모가 [options] 로 어느 쪽인지 결정)
 * Step 2: 측정 대상 장비 선택(합산). 대표장비는 기본 선택+잠금, 추가로 더 선택.
 *         권(BOOK_PRODUCTION)은 플로터 제외(job 미생성).
 * Step 3: 목표 입력 — 모든 지표 공통. 도입 전(이전값)·목표값을 입력한다.
 *         needsDailyTarget 인 지표는 추가로 "하루 가동 목표 시간"을 받아 % 환산 분모로 쓴다.
 *
 * 장비 측정 소스가 없는 지표(설계변경)는 Step 2 를 건너뛰고 1 → 3 으로 간다.
 */
interface SelectableMachine {
  machineId: number;
  modelName: string;
  machineType?: string;
  ipAddress: string;
}

/** 슬롯 지표 옵션 — 부모(CategoryContent)가 WORKSHOP(SbizType)/FACTORY(KpiType) 중 하나로 채워 전달. */
export interface KpiSlotOption {
  /** SbizType 또는 KpiType 의 enum 문자열 값 */
  value: string;
  group: "P" | "D" | "Q";
  /** 정부 보고 코드 — WORKSHOP 만 값, FACTORY 는 undefined */
  code?: string;
  label: string;
  desc: string;
  unit: string;
  direction: "INCREASE" | "DECREASE";
  /** 장비 측정 소스 없음(설계변경 등) — Step 2 건너뜀 */
  needsMachine: boolean;
  /** 목표 스텝에서 "하루 가동 목표 시간" 별도 입력 필요(WORKSHOP 가동률 전용) */
  needsDailyTarget?: boolean;
}

interface KpiSlotModalProps {
  /** 1 = P 그룹 슬롯, 2 = D·Q 그룹 슬롯 */
  slot: 1 | 2;
  /** 선택 가능한 지표 목록 (부모가 플러그인 타입에 맞춰 전달) */
  options: KpiSlotOption[];
  /** 이미 등록된 지표 값 (중복 추가 방지, existingValues.includes(option.value)) */
  existingValues: string[];
  /** 회사 장비 (장비 선택지) */
  machines: SelectableMachine[];
  /** 대표장비 (기본 선택 + 잠금) */
  targetMachineId: number | null;
  onClose: () => void;
  onConfirm: (
    slot: 1 | 2,
    value: string,
    machineIds: number[],
    opts?: { baseValue: number; goalValue: number; dailyTargetMinutes?: number },
  ) => Promise<void> | void;
}

/** 권(BOOK_PRODUCTION) 은 플로터에서 job 이 생성되지 않아 선택 불가. */
const BOOK_PRODUCTION_VALUES = new Set(["BOOK_PRODUCTION"]);

const Badge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    className="inline-flex items-center px-2 py-[2px] rounded text-[11px] font-medium"
    style={{ background: "#E8EFFF", color: "#4285F4" }}
  >
    {children}
  </span>
);

const KpiSlotModal: React.FC<KpiSlotModalProps> = ({
  slot,
  options,
  existingValues,
  machines,
  targetMachineId,
  onClose,
  onConfirm,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  // 대표장비 기본 선택
  const [selectedMachineIds, setSelectedMachineIds] = useState<Set<number>>(
    () => new Set(targetMachineId != null ? [targetMachineId] : []),
  );
  // 목표 스텝 입력 (전체 지표 공통: 이전값/목표값, needsDailyTarget 지표는 추가로 하루 가동 목표 시간)
  const [targetHours, setTargetHours] = useState("");
  const [baseRate, setBaseRate] = useState("");
  const [goalRate, setGoalRate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedOption = options.find((o) => o.value === selectedValue) ?? null;
  const needsMachine = selectedOption?.needsMachine ?? false;
  const isBook = selectedValue != null && BOOK_PRODUCTION_VALUES.has(selectedValue);
  const needsDailyTarget = selectedOption?.needsDailyTarget ?? false;
  const valueUnit = selectedOption?.unit ?? "";
  const isDecrease = selectedOption?.direction === "DECREASE";
  const isPlotter = (m: SelectableMachine) =>
    (m.machineType ?? "").toUpperCase() === "PLOTTER";

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const submit = async (
    machineIds: number[],
    opts?: { baseValue: number; goalValue: number; dailyTargetMinutes?: number },
  ) => {
    if (selectedValue == null) return;
    setIsSubmitting(true);
    try {
      await onConfirm(slot, selectedValue, machineIds, opts);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (selectedValue === null) {
      alert("지표를 선택해주세요.");
      return;
    }
    if (!needsMachine) {
      // 장비 측정 없는 지표(설계변경) → 장비 단계 건너뛰고 목표 입력으로
      setStep(3);
      return;
    }
    setStep(2);
  };

  const toggleMachine = (machineId: number) => {
    setSelectedMachineIds((prev) => {
      const next = new Set(prev);
      if (next.has(machineId)) next.delete(machineId);
      else next.add(machineId);
      return next;
    });
  };

  const handleConfirmMachines = () => {
    const ids = Array.from(selectedMachineIds);
    if (ids.length === 0) {
      alert("측정 대상 장비를 1대 이상 선택해주세요.");
      return;
    }
    setStep(3);
  };

  const handleConfirmTarget = () => {
    const machineIds = needsMachine ? Array.from(selectedMachineIds) : [];
    const base = Number(baseRate);
    const goal = Number(goalRate);
    if (baseRate.trim() === "" || goalRate.trim() === "" || !Number.isFinite(base) || !Number.isFinite(goal)) {
      alert("이전값과 목표값을 입력해주세요.");
      return;
    }
    if (needsDailyTarget) {
      const h = Number(targetHours);
      if (!(h > 0)) {
        alert("하루 가동 목표 시간(시간)을 입력해주세요.");
        return;
      }
      submit(machineIds, { baseValue: base, goalValue: goal, dailyTargetMinutes: Math.round(h * 60) });
      return;
    }
    submit(machineIds, { baseValue: base, goalValue: goal });
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[200000] flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[12px] w-[520px] max-w-[calc(100vw-32px)] p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 스텝 인디케이터 */}
        <div className="flex items-center gap-2 mb-4 text-[11.5px]">
          <span className={`px-2 py-0.5 rounded-full ${step === 1 ? "bg-[#4285F4] text-white" : "bg-[#E8EFFF] text-[#4285F4]"}`}>
            1 지표
          </span>
          {needsMachine && (
            <>
              <span className="text-[#ccc]">›</span>
              <span className={`px-2 py-0.5 rounded-full ${step === 2 ? "bg-[#4285F4] text-white" : "bg-[#F0F0F0] text-[#999]"}`}>
                2 측정 장비
              </span>
            </>
          )}
          <span className="text-[#ccc]">›</span>
          <span className={`px-2 py-0.5 rounded-full ${step === 3 ? "bg-[#4285F4] text-white" : "bg-[#F0F0F0] text-[#999]"}`}>
            {needsMachine ? "3" : "2"} 목표
          </span>
        </div>

        {step === 1 ? (
          <>
            <h3 className="text-[16px] font-bold mb-1">지표 선택</h3>
            <p className="text-[12.5px] text-[#888] mb-5">
              {slot === 1
                ? "P 그룹 (생산성) 에서 1개의 지표를 선택하세요."
                : "D 그룹 (납기) 또는 Q 그룹 (품질) 에서 1개의 지표를 선택하세요."}
            </p>
            <div className="space-y-2 mb-5">
              {options.map((it) => {
                const isChecked = selectedValue === it.value;
                const isDuplicate = existingValues.includes(it.value);
                return (
                  <label
                    key={it.value}
                    className={`border rounded-[8px] p-3 flex items-start gap-3 ${
                      isDuplicate
                        ? "cursor-not-allowed opacity-50 border-[#E0E0E0]"
                        : "cursor-pointer hover:bg-[#F5F8FF]"
                    } ${isChecked && !isDuplicate ? "border-[#4285F4] bg-[#F5F8FF]" : isDuplicate ? "" : "border-[#D9D9D9]"}`}
                  >
                    <input
                      type="radio"
                      name="kpiSlotOption"
                      checked={isChecked}
                      disabled={isDuplicate}
                      onChange={() => setSelectedValue(it.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <span className="font-medium text-[13px]">{it.label}</span>
                        <Badge>{it.group}</Badge>
                        {it.code && <Badge>코드 {it.code}</Badge>}
                        {isDuplicate && <span className="text-[10.5px] text-[#888]">· 이미 추가됨</span>}
                      </div>
                      <div className="text-[11px] text-[#888]">{it.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-white text-[#374151] border border-[#D1D5DB] hover:bg-[#F9FAFB] disabled:opacity-60"
              >
                취소
              </button>
              <button
                onClick={handleNext}
                disabled={isSubmitting || selectedValue === null}
                className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-[#252525] text-white hover:bg-[#333] disabled:opacity-60"
              >
                다음
              </button>
            </div>
          </>
        ) : step === 2 ? (
          <>
            <h3 className="text-[16px] font-bold mb-1">측정 대상 장비</h3>
            <p className="text-[12.5px] text-[#888] mb-4">
              선택한 장비들의 측정값을 <b>합산</b>합니다. 대표장비는 기본 포함(해제 불가).
              {isBook && " 책자(권)는 플로터에서 작업이 생성되지 않아 선택할 수 없습니다."}
            </p>
            <div className="space-y-1.5 mb-5 max-h-[300px] overflow-y-auto">
              {machines.length === 0 ? (
                <div className="text-[12.5px] text-[#888] py-6 text-center">
                  등록된 장비가 없습니다.
                </div>
              ) : (
                machines.map((m) => {
                  const isTarget = m.machineId === targetMachineId;
                  const blockedByBook = isBook && isPlotter(m);
                  const disabled = isTarget || blockedByBook;
                  const checked = selectedMachineIds.has(m.machineId);
                  return (
                    <label
                      key={m.machineId}
                      className={`border rounded-[8px] px-3 py-2 flex items-center gap-3 ${
                        disabled
                          ? "cursor-not-allowed border-[#E0E0E0] " +
                            (isTarget ? "bg-[#F5F8FF]" : "opacity-50")
                          : "cursor-pointer hover:bg-[#F5F8FF] " +
                            (checked ? "border-[#4285F4] bg-[#F5F8FF]" : "border-[#D9D9D9]")
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleMachine(m.machineId)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-medium text-[13px] truncate">{m.modelName}</span>
                          {m.machineType && <Badge>{m.machineType}</Badge>}
                          {isTarget && <span className="text-[10.5px] text-[#4285F4]">· 대표장비</span>}
                          {blockedByBook && <span className="text-[10.5px] text-[#F24A4A]">· 권 측정 불가</span>}
                        </div>
                        <div className="text-[11px] text-[#888]">{m.ipAddress}</div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
            <div className="flex justify-between gap-2">
              <button
                onClick={() => setStep(1)}
                disabled={isSubmitting}
                className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-white text-[#374151] border border-[#D1D5DB] hover:bg-[#F9FAFB] disabled:opacity-60"
              >
                이전
              </button>
              <button
                onClick={handleConfirmMachines}
                disabled={isSubmitting}
                className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-[#252525] text-white hover:bg-[#333] disabled:opacity-60"
              >
                {isSubmitting ? "처리 중…" : `다음 (${selectedMachineIds.size}대)`}
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="text-[16px] font-bold mb-1">목표 입력</h3>
            <p className="text-[12.5px] text-[#888] mb-4">
              {selectedOption?.label} 의 도입 전 값(이전값)과 목표값을 입력하세요.
              {needsDailyTarget
                ? " 그래프는 하루 가동 시간으로 표시되고, 가동률(%)은 가동시간 ÷ 목표시간으로 환산됩니다."
                : isDecrease
                  ? " 목표값은 이전값보다 낮을수록 개선입니다."
                  : " 목표값은 이전값보다 높을수록 개선입니다."}
            </p>
            <div className="space-y-3 mb-5">
              {needsDailyTarget && (
                <div>
                  <label className="text-[12.5px] text-[#555] block mb-1">
                    하루 가동 목표 시간 <span className="text-[#888]">(예: 5)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      step="0.5"
                      value={targetHours}
                      onChange={(e) => setTargetHours(e.target.value)}
                      placeholder="5"
                      className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] w-28"
                    />
                    <span className="text-[12.5px] text-[#888]">시간/일</span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[12.5px] text-[#555] block mb-1">
                    이전값 (도입 전){valueUnit && <span className="text-[#888]"> · {valueUnit}</span>}
                  </label>
                  <input
                    type="number"
                    value={baseRate}
                    onChange={(e) => setBaseRate(e.target.value)}
                    placeholder="0"
                    className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] w-full"
                  />
                </div>
                <div>
                  <label className="text-[12.5px] text-[#555] block mb-1">
                    목표값{valueUnit && <span className="text-[#888]"> · {valueUnit}</span>}
                  </label>
                  <input
                    type="number"
                    value={goalRate}
                    onChange={(e) => setGoalRate(e.target.value)}
                    placeholder={needsDailyTarget ? "90" : ""}
                    className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] w-full"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-between gap-2">
              <button
                onClick={() => setStep(needsMachine ? 2 : 1)}
                disabled={isSubmitting}
                className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-white text-[#374151] border border-[#D1D5DB] hover:bg-[#F9FAFB] disabled:opacity-60"
              >
                이전
              </button>
              <button
                onClick={handleConfirmTarget}
                disabled={isSubmitting}
                className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-[#252525] text-white hover:bg-[#333] disabled:opacity-60"
              >
                {isSubmitting ? "추가 중…" : "추가"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default KpiSlotModal;
